import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, Gift, Trophy, QrCode, Users, ArrowRight, Sparkles } from "lucide-react";

const STEPS = [
  {
    icon: Users,
    title: "Your one-tap referrals",
    body: "Hit New Referral, share your unique code on WhatsApp, and every closed referral earns you reward points automatically.",
  },
  {
    icon: Sparkles,
    title: "Your points balance",
    body: "See your live points balance on the right. 1 point = ₹1 at any Veloskin tie-up shop.",
  },
  {
    icon: Trophy,
    title: "Agility Tier progress",
    body: "Silver → Gold → Platinum → Black → Elite. Every tier upgrade unlocks a special gift from Veloskin.",
  },
  {
    icon: QrCode,
    title: "Redeem at tie-up shops",
    body: "Tap Redeem at tie-up shop to generate a QR + code. Show it to any Veloskin partner shop to instantly redeem your points.",
  },
  {
    icon: Gift,
    title: "You're all set",
    body: "Refer, earn, redeem. Come back any time — this tour won't show up again.",
  },
];

export default function FirstLoginTour({ userId }) {
  const key = userId ? `velo_tour_done_${userId}` : null;
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!key) return undefined;
    if (localStorage.getItem(key)) return undefined;

    let cancelled = false;
    let timer = null;

    // Only start the tour once the redemption PIN is set — otherwise the
    // forced PinGate modal would render underneath and clicks would fall
    // through to it, triggering "PIN must be 4 digits" / "not match" errors.
    const tryStart = async () => {
      try {
        const { data } = await api.get("/me/pin-status");
        if (cancelled) return;
        if (data?.has_pin) {
          timer = setTimeout(() => { if (!cancelled) setOpen(true); }, 400);
        } else {
          // PIN not yet set — poll every 2s so we start the tour the moment
          // the user finishes the PIN setup step.
          timer = setTimeout(tryStart, 2000);
        }
      } catch {
        // If we can't read the status (network hiccup), fall back to showing
        // the tour after a short delay rather than blocking it forever.
        timer = setTimeout(() => { if (!cancelled) setOpen(true); }, 1200);
      }
    };
    tryStart();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [key]);

  const dismiss = () => {
    if (key) localStorage.setItem(key, "1");
    setOpen(false);
  };

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;
  return (
    <div
      data-testid="first-login-tour"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
    >
      <div className="velo-card w-full max-w-md p-6 sm:p-8 relative border border-pink-500/30 shadow-2xl">
        <button
          data-testid="btn-tour-dismiss"
          type="button"
          onClick={dismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
          aria-label="Dismiss tour"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <div className="w-12 h-12 rounded-xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-pink-400" strokeWidth={1.75} />
        </div>
        <div className="text-[11px] uppercase tracking-widest text-pink-400 mb-1">
          Step {step + 1} of {STEPS.length}
        </div>
        <h2 id="tour-title" className="text-2xl text-white font-light tracking-tight mb-3">{s.title}</h2>
        <p className="text-slate-300 leading-relaxed text-sm">{s.body}</p>
        <div className="flex gap-1.5 mt-5">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${i === step ? "w-8 bg-pink-400" : i < step ? "w-4 bg-pink-500/40" : "w-4 bg-white/10"}`}
            />
          ))}
        </div>
        <div className="flex items-center justify-between mt-6 gap-3">
          <button
            data-testid="btn-tour-skip"
            type="button"
            onClick={dismiss}
            className="text-xs text-slate-400 hover:text-slate-200 underline underline-offset-2"
          >
            Skip tour
          </button>
          <Button
            data-testid={isLast ? "btn-tour-finish" : "btn-tour-next"}
            type="button"
            onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}
            className="rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
          >
            {isLast ? "Got it" : "Next"}
            {isLast ? <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={2} /> : <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2} />}
          </Button>
        </div>
      </div>
    </div>
  );
}
